import { OpenGame } from 'shared';
import { Button } from '../../ui/Button';
import { Table, TableColumn } from '../../ui/Table';
import { Text } from '../../ui/Text';
import { Skeleton } from '../../ui/Skeleton';
import { ColorToken, token } from '../../ui/theme';

/*
 * OpenGamesTable — the lobby's list of joinable games, one sortable row each.
 */

// Status badge + join-button wording per joinKind from the lobby controller.
const JOIN_KIND: Record<
  OpenGame['joinKind'],
  { label: string; color: ColorToken; action: string }
> = {
  open: { label: 'Waiting', color: 'color-win', action: 'Join' },
  resume: { label: 'Seat open', color: 'color-gold', action: 'Resume' },
  takeover: { label: 'In progress', color: 'color-accent', action: 'Take a seat' },
};

interface Props {
  games: OpenGame[];
  /** The first fetch hasn't returned yet — show placeholder rows, not "none". */
  loading?: boolean;
  joiningId?: string;
  onJoin: (gameId: string) => void;
}

export const OpenGamesTable = ({ games, loading, joiningId, onJoin }: Props) => {
  const columns: TableColumn<OpenGame>[] = [
    {
      label: 'Game',
      width: '34%',
      sortValue: (game) => game.title,
      render: (game) => (
        <Text variant="value" style={{ fontSize: token('font-size-sm') }} title={game.title}>
          {game.title}
        </Text>
      ),
    },
    {
      label: 'Host',
      width: '16%',
      sortValue: (game) => game.hostName,
      render: (game) => <span style={{ color: token('color-muted') }}>{game.hostName}</span>,
    },
    {
      label: 'Players',
      width: '10%',
      align: 'center',
      sortValue: (game) => game.seatedCount,
      render: (game) => `${game.seatedCount}/${game.totalSeats}`,
    },
    {
      label: 'Score',
      width: '8%',
      align: 'center',
      sortValue: (game) => game.scoreToWin,
      render: (game) => game.scoreToWin,
    },
    {
      label: 'Status',
      width: '14%',
      align: 'center',
      sortValue: (game) => game.joinKind,
      render: (game) => {
        const kind = JOIN_KIND[game.joinKind];
        return <Badge label={kind.label} color={token(kind.color)} />;
      },
    },
    {
      label: 'Code',
      width: '9%',
      align: 'center',
      render: (game) => <span style={codeCell}>{game.code}</span>,
    },
    {
      label: '',
      width: '9%',
      align: 'right',
      render: (game) => (
        <Button variant="ghost" size="sm" onClick={() => onJoin(game.id)} disabled={!!joiningId}>
          {joiningId === game.id ? 'Joining…' : JOIN_KIND[game.joinKind].action}
        </Button>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      rows={games}
      rowKey={(game) => game.id}
      minWidth={860}
      empty={loading ? <LoadingRows /> : <EmptyState />}
    />
  );
};

const LoadingRows = () => (
  <div style={{ display: 'grid', gap: 'var(--space-3)', padding: 'var(--space-4) 0' }}>
    {[0, 1, 2].map((i) => (
      <Skeleton key={i} height={28} />
    ))}
  </div>
);

const EmptyState = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: 'var(--space-7) 0',
      gap: 'var(--space-2)',
    }}
  >
    <span
      style={{
        fontSize: '48px',
        color: token('color-border'),
        lineHeight: 1,
        marginBottom: 'var(--space-2)',
      }}
    >
      ♠
    </span>
    <Text variant="value">No open games</Text>
    <Text variant="muted" style={{ textAlign: 'center' }}>
      Start one and wait for opponents to join
    </Text>
  </div>
);

const Badge = ({ label, color }: { label: string; color: string }) => (
  <span
    style={{
      display: 'inline-block',
      fontSize: token('font-size-xs'),
      fontWeight: 700,
      letterSpacing: token('letter-spacing-wide'),
      color,
      border: `1px solid ${color}`,
      borderRadius: token('radius-pill'),
      padding: '1px 8px',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </span>
);

const codeCell: React.CSSProperties = {
  fontFamily: 'monospace',
  letterSpacing: '2px',
  color: token('color-muted'),
};
